import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SharedAccountNotice from '../SharedAccountNotice';
import RouteFlowMap from './RouteFlowMap';
import SelectionPanel, {
  NavSection,
} from '../../common/configPanel/SelectionPanel';
import SelectInput from '../../common/SelectInput';
import ActionSectionForm from '../../common/configPanel/ActionSectionForm';
import { useConfigEditor } from '../../common/configPanel/useConfigEditor';
import {
  isAuth,
  isDormantAction,
  slugOf,
} from '../../common/configPanel/actionSetup';
import { isActionVisible } from '../../common/SchemaFormTemplates/checkSchema';
import {
  ADVANCED_CAT,
  buildActionNav,
  flowActionCounts,
} from '../../common/configPanel/buildActionNav';
import { AccountBundle, FlowDirectionKey, Selection } from './flowMapTypes';
import {
  buildFlowArrows,
  buildGraph,
  hasType,
  useAccountBundles,
} from './useFlowGraph';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../utils/useIsMobile';
import { getRouteKind } from '../../common/routeKind';
import RouteDetailsWebhook, {
  RouteDetailsWebhookHandle,
} from '../RouteDetailsWebhook';
import { copyToClipboard } from '../../common/copyToClipboard';
import { CopyToClipboard, OpenInNewIcon } from '../../common/ProjectIcons';
import {
  getReverseRoutes,
  getRouteApiKey,
  updateConnection,
} from '../../../api/routes/routesApi';
import UnsavedSectionPrompt, {
  SectionPrompt,
} from '../../common/configPanel/UnsavedSectionPrompt';
import HowToConnect from '../../common/configPanel/HowToConnect';
import {
  computeNeedsSetup,
  dormantFlowTypes,
  exemptSlugsFor,
} from './needsSetup';
import { payloadParsingNotRequired } from '../../common/configPanel/tempExemptions';
import { baseUrlPolicy } from './baseUrlPolicy';
import { validateBaseUrl } from '../../destinations/validateBaseUrl';
import EarthRangerSiteInput from '../../destinations/EarthRangerSiteInput';
import {
  constructEarthRangerUrl,
  extractEarthRangerSiteName,
  isEarthRangerDestinationType,
} from '../../destinations/earthRangerUrl';
import { buttonClasses, onTealText } from '../../common/tailwindClassnames';
import ModalShell from '../../common/ModalShell';
// The portal's canonical switch (headless-ui + the shared switchContainer classes),
// reused here so the direction toggle looks like every other toggle in the app.
import CustomCheckbox from '../../common/SchemaFormTemplates/CustomCheckbox';
import { ActionPillButton } from '../flowIcons';
import { Rule } from '../transformationRules/fieldMappingTypes';
import { routeRecordId } from '../routeDisplayName';
import { arrowOffersRules } from './arrowRules';

// The selected flow-map node used to live in the URL
// (…/configurations/account/<id>). It no longer does. Route pages are addressed
// by the provider integration id, so that segment ended up repeating the very
// id already in the path, and a Route only needs three addresses now:
// /routes/:id, /routes/:id/sources and /routes/:id/logs.
//
// Callers that need a particular panel open after an action (adding a
// destination, an old /provider bookmark) ask for it through the router's
// `state`, the same channel `highlightAddDestination` already uses. What this
// gives up, deliberately, is a paste-able link to one specific panel.
export function useFlowSelection(): {
  selection: Selection;
  select: (next: Selection) => void;
} {
  const location = useLocation();
  const requested = (location.state as any)?.flowSelection as
    Selection | undefined;
  const [selection, setSelection] = useState<Selection>(
    requested ?? { kind: 'none' },
  );
  // A request can arrive at a page that is already mounted (the flow map stays
  // up while a destination is added), so it has to be honoured on change and
  // not only at mount. Identity is the right test: every navigation carries a
  // fresh state object, while a re-render carries the same one — comparing by
  // value would re-apply the request and fight the user's next click.
  const lastHandled = useRef<Selection | undefined>(requested);
  useEffect(() => {
    if (!requested || requested === lastHandled.current) return;
    lastHandled.current = requested;
    setSelection(requested);
  }, [requested]);

  return { selection, select: setSelection };
}

// The copy-ID control under a header's name. The label flips to "Copied" for a moment,
// and only when the copy actually succeeded.
export const CopyIdButton = ({
  id,
  testId,
  label,
}: {
  id: string;
  testId: string;
  label: string;
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={async () => {
        if (!(await copyToClipboard(id, t))) return;
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 2000);
      }}
      className="flex flex-shrink-0 items-center gap-1 text-ui-text-primary-text"
    >
      {copied ? t('Copied!') : label}
      <CopyToClipboard />
    </button>
  );
};

const ConfigurationsTab = (props: any) => {
  const { route, routeProvider, token, currentMemberIsAdmin } = props;
  const { t } = useTranslation();
  const providerId: string = routeProvider?.id || route?.provider?.id;
  const isMobile = useIsMobile();
  const destinations: any[] = React.useMemo(
    () => route?.destinations || [],
    [route?.destinations],
  );
  const accountIds = React.useMemo(
    () => [providerId, ...destinations.map((d: any) => d.id)].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [providerId, destinations.map((d: any) => d.id).join(',')],
  );

  // The parent fetches the provider integration itself — seed the bundle map
  // with it instead of re-fetching the same record.
  const { bundles, loading, failed, reload } = useAccountBundles(
    token,
    accountIds,
    providerId,
    routeProvider,
  );
  // Links out of this tab must use the id the page is ALREADY addressed by
  // (the parent's :route_id), never a re-derived one: deriving it flipped
  // between the route-record and provider-integration id spaces for records
  // reached through routing_rules, so a link pointed at a different spelling
  // of the same page. Route URLs carry the provider INTEGRATION id —
  // `route.id` on the merged connection object, NOT `default_route.id`.
  const routeUrlId: string = props.routeUrlId || route?.id;
  const { selection, select } = useFlowSelection();

  const selectedAccountId =
    selection.kind === 'account' ? selection.integrationId : undefined;
  const selectedArrowId =
    selection.kind === 'flow'
      ? `${selection.destinationId}-${selection.direction}`
      : undefined;

  // Structural graph: every input is either a primitive or identity-stable
  // parent state, so a plain dependency array works. Selection is layered on
  // afterwards so clicking around never rebuilds nodes and edges.
  const routeEnabled = routeProvider?.enabled !== false;
  const structural = React.useMemo(() => {
    const built = buildFlowArrows({
      providerId,
      provider: route?.provider,
      destinations,
      reverseRoutesBySite: props.reverseRoutesBySite || {},
      providerCanReceive: !!props.providerCanReceive,
      routeEnabled,
      // undefined while the destination's integration hasn't loaded — the
      // arrow is drawn unavailable but claims no reason for it.
      destinationCanSend: (destId: string) =>
        bundles[destId] ? hasType(bundles[destId].actions, 'pull') : undefined,
      t,
    });

    // Who still has work to do — one shared answer for the node badges and
    // the Quick Start steps. All badges live on nodes in this model.
    const setup = computeNeedsSetup({
      providerId,
      routeProvider,
      bundles,
      destinations,
      arrows: built,
    });
    const { needsSetupAccounts } = setup;

    const graph = buildGraph({
      provider: route?.provider,
      destinations,
      arrows: built,
      bundles,
      destinationsWithRules: props.destinationsWithRules || new Set<string>(),
      reverseDestinationsWithRules:
        props.reverseDestinationsWithRules || new Set<string>(),
      needsSetupAccounts,
      vertical: isMobile,
      t,
    });
    return { ...graph, ...setup, arrows: built };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    providerId,
    route?.provider,
    routeProvider,
    destinations,
    bundles,
    routeEnabled,
    props.reverseRoutesBySite,
    props.providerCanReceive,
    props.destinationsWithRules,
    props.reverseDestinationsWithRules,
    isMobile,
    t,
  ]);

  const arrows = structural.arrows;

  // The Quick Start banner lives on the page above this tab and has no view of
  // the per-account configurations, so report what is still unconfigured.
  const { onNeedsSetupChange } = props;
  const needsSetupCount = loading ? null : structural.needsSetupCount;
  const providerNeedsSetup = structural.providerNeedsSetup;
  const destinationsNeedSetup = structural.destinationsNeedSetup;
  // Nothing loaded at all (no token yet, or it expired) is not a report: with an
  // empty bundle map every account would read as unconfigured and the banner
  // would flip a finished Route to all-warnings.
  const anyBundleLoaded = Object.keys(bundles).length > 0;
  useEffect(() => {
    if (needsSetupCount !== null && anyBundleLoaded)
      onNeedsSetupChange?.({
        total: needsSetupCount,
        provider: providerNeedsSetup,
        destinations: destinationsNeedSetup,
      });
  }, [
    needsSetupCount,
    providerNeedsSetup,
    destinationsNeedSetup,
    anyBundleLoaded,
    onNeedsSetupChange,
  ]);

  const { nodes, edges } = React.useMemo(
    () => ({
      nodes: structural.nodes.map((n: any) =>
        n.id === selectedAccountId
          ? { ...n, data: { ...n.data, isSelected: true } }
          : n,
      ),
      edges: structural.edges.map((e: any) =>
        e.id === selectedArrowId
          ? { ...e, data: { ...e.data, isSelected: true } }
          : e,
      ),
    }),
    [structural, selectedAccountId, selectedArrowId],
  );

  // Confirmations render through the shared ModalShell rather than window.confirm,
  // so they match every other dialog in the portal.
  type Confirmation = {
    title: string;
    body: string;
    confirmLabel: string;
    destructive?: boolean;
    onConfirm: () => void;
  };
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  // The Save-or-discard question, shared with every configuration screen.
  const [sectionPrompt, setSectionPrompt] = useState<SectionPrompt | null>(
    null,
  );
  // The webhook editor holds its own draft; the panel footer drives its save.
  const webhookRef = useRef<RouteDetailsWebhookHandle>(null);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const navigate = useNavigate();

  // A map with a single node has nothing else to click — open its panel instead
  // of asking the user to click the only thing there is. Once per mount only:
  // deselecting is also a deliberate act (removing the last destination ends
  // with select({kind:"none"})), and re-firing dragged the user back into the
  // form they had just left.
  const autoSelectSettled = useRef(false);
  useEffect(() => {
    // Anything other than "an empty map with nothing selected" means the user
    // has somewhere to be: a selection of their own, or destinations to click.
    // Latching here is what keeps a later deselect (removing the last
    // destination ends with select({kind:"none"})) from being overridden.
    if (selection.kind !== 'none' || destinations.length > 0) {
      autoSelectSettled.current = true;
      return;
    }
    if (autoSelectSettled.current || !providerId) return;
    autoSelectSettled.current = true;
    // Selecting in place, not navigating: a Route with nothing on its map
    // should open its provider's panel, but that is a view detail and no
    // longer belongs in the URL.
    select({ kind: 'account', integrationId: providerId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.kind, providerId, destinations.length]);

  // ---- unsaved edits, stashed per account and PATCHed on Save -------------
  // Shared with every other configuration screen (destination page, wizard) so
  // "Save" always means "PATCH this one section".
  const editor = useConfigEditor({
    token,
    t,
    onSaved: (id: string) => reload(id),
  });
  const { dirtyIds, anyDirty } = editor;

  // Switching to a different node/arrow throws away whatever is stashed for the
  // current one, so ask first. Switching sections inside the same selection keeps
  // the same pending stash and never prompts.
  const guardedSelect = (next: Selection) => {
    const sameAccount =
      next.kind === 'account' &&
      selection.kind === 'account' &&
      next.integrationId === selection.integrationId;
    const sameFlow =
      next.kind === 'flow' &&
      selection.kind === 'flow' &&
      next.destinationId === selection.destinationId &&
      next.direction === selection.direction;
    // The General draft is unsaved state too, even though it is not an
    // action configuration. The same Save-or-discard question a section
    // switch asks — offering only "discard" made the user cancel, save by
    // hand, and come back to click where they wanted to go.
    if ((anyDirty || generalDraft !== null) && !sameAccount && !sameFlow) {
      return setSectionPrompt({
        onSave: savePendingEdits,
        onDiscard: discardPendingEdits,
        proceed: () => select(next),
      });
    }
    select(next);
  };

  // Leaving the Route entirely deserves the same prompt as moving between
  // selections — unsaved edits would just vanish when the panel unmounts.
  const guardedNavigate = (to: string) => {
    if (anyDirty || generalDraft !== null) {
      return setSectionPrompt({
        onSave: savePendingEdits,
        onDiscard: discardPendingEdits,
        proceed: () => navigate(to),
      });
    }
    navigate(to);
  };

  // ---- other Routes sharing a destination account -------------------
  // Fetched lazily for the destination whose panel is open (the notice only
  // renders there), and cached per id — never one call per destination upfront.
  const [sharedBySite, setSharedBySite] = useState<
    Record<string, { id: string; name: string }[]>
  >({});
  const sharedFetched = useRef<Set<string>>(new Set());
  const selectedDestId =
    selection.kind === 'account' && selection.integrationId !== providerId
      ? selection.integrationId
      : undefined;
  useEffect(() => {
    if (!token || !selectedDestId || sharedFetched.current.has(selectedDestId))
      return;
    sharedFetched.current.add(selectedDestId);
    // Same reasoning as the api-key fetch: keyed by id, so a late response is
    // safe to store. Dropping it while the id stayed latched meant the
    // shared-account warning never appeared for that destination again — the
    // UI would then affirm that nothing else is affected.
    (async () => {
      try {
        const data = await getReverseRoutes(token, selectedDestId);
        const others = (data?.results || data || [])
          .filter((c: any) => c?.id && c.id !== route?.id)
          // No name fallback to the raw id: a Route the user can't
          // resolve renders as a masked count, never as an identifier.
          // Route URLs address the provider integration id, which is
          // `c.id` here; the route record still has to exist for there
          // to be a Route page at all.
          .map((c: any) => ({
            id: routeRecordId(c) ? c.id : undefined,
            name: c.provider?.name,
          }))
          .filter((c: any) => c.id);
        setSharedBySite((prev) => ({ ...prev, [selectedDestId]: others }));
      } catch (e) {
        // Allow a retry on the next visit to this panel.
        sharedFetched.current.delete(selectedDestId);
        console.error('failed to load routes sharing site', selectedDestId, e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedDestId, route?.id]);

  // ---- section content ---------------------------------------------------
  // `showRequiredHints` follows the same rule as the nav "!" badge: a section
  // the nav doesn't badge must not paint red "Required" hints either.
  const renderActionForm = (
    bundle: AccountBundle,
    action: any,
    editable: boolean,
    showRequiredHints: boolean,
  ) => (
    <ActionSectionForm
      target={bundle}
      action={action}
      editable={editable}
      showRequiredHints={showRequiredHints}
      userIsAdmin={currentMemberIsAdmin}
      token={token}
      formVersion={editor.formVersion[bundle.id] || 0}
      errors={editor.formErrors[bundle.id]?.[action.id]}
      formData={editor.formDataFor(bundle, action)}
      getFormData={() => editor.formDataFor(bundle, action)}
      onChange={(data: any, fieldId?: string) =>
        editor.snapshotChange(bundle, action.id, data, fieldId)
      }
      test={
        props.onTestAction
          ? {
              onTestAction: props.onTestAction,
              loading: props.testActionLoading,
              response: props.testActionResponse,
            }
          : undefined
      }
    />
  );

  // Field mapping lives on the Route (per flow), not on the account, and is
  // edited in the existing transformation-rules side panel.
  // `direction` says which flow's rules these are: "out" = this Route
  // (provider → site), "in" = the reverse Route (site → provider), whose rules
  // live on that other route record.
  const fieldMappingSection = (
    dest: any,
    direction: FlowDirectionKey = 'out',
  ) => {
    const rule: Rule | null = dest
      ? direction === 'in'
        ? props.getReverseRule?.(dest.id)
        : props.getRule(dest.id)
      : null;
    const openRules = () => props.onOpenRules(dest, direction);
    if (!rule)
      return (
        <>
          <p className="mx-auto mt-5 max-w-xl text-center text-sm text-ui-text-primary-text">
            {direction === 'in'
              ? t('Rules change the data on its way back to this provider')
              : t('Rules change the data on its way to this destination')}
          </p>
          <ActionPillButton label={t('Add Rules')} onClick={openRules} />
        </>
      );
    const mapEntries = rule.mapping.map ? Object.entries(rule.mapping.map) : [];
    return (
      <>
        <div className="mb-2 flex items-center gap-3">
          <span className="text-sm font-semibold text-ui-text-primary-text">
            {t('Field mapping')}
          </span>
          <button
            className={`ml-auto ${buttonClasses.inlineLink}`}
            onClick={openRules}
          >
            {t('Edit')} ▸
          </button>
        </div>
        <div className="rounded border border-unlisted-gray-lines bg-greyscale-11 px-3 py-3 text-sm md:px-4">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="rounded bg-teal-t10 px-2 py-0.5 text-xs font-semibold text-green-1">
              {rule.streamType === 'ev' ? t('Events') : t('Observations')}
            </span>
            <span className="min-w-0 break-all font-mono text-xs text-ui-text-primary-text">
              {rule.mapping.provider_field || t('(source value)')} →{' '}
              {rule.mapping.destination_field}
            </span>
          </div>
          {mapEntries.length > 0 && (
            <div className="flex flex-col gap-1">
              {mapEntries.slice(0, 4).map(([from, to]) => (
                <div
                  key={from}
                  className="flex flex-wrap items-center gap-2 font-mono text-xs text-ui-text-primary-text"
                >
                  <span className="break-all rounded border border-unlisted-gray-lines bg-white px-2 py-0.5">
                    {from}
                  </span>
                  <span className="font-bold text-green-1">→</span>
                  <span className="break-all rounded border border-unlisted-gray-lines bg-white px-2 py-0.5">
                    {String(to)}
                  </span>
                </div>
              ))}
              {mapEntries.length > 4 && (
                <span className="text-xs text-ui-text-primary-text">
                  {t('+ {{count}} more…', { count: mapEntries.length - 4 })}
                </span>
              )}
            </div>
          )}
          {rule.mapping.default !== undefined && (
            <div className="mt-1 break-all font-mono text-xs text-ui-text-primary-text">
              {t('default')}: {String(rule.mapping.default)}
            </div>
          )}
        </div>
      </>
    );
  };

  // ---- how to connect (provider API key + endpoint) -----------------------
  // Push/webhook providers are configured by POSTing to Gundi, so their panel
  // leads with the endpoint + API key that used to live on the Provider tab.
  // Draft of the selected account's General fields (name / workspace /
  // endpoint); null means "not edited". Renaming a Connection here never
  // touches the Route's own name — they are decoupled. The draft is dropped
  // when the selection moves: guardedSelect can't catch a selection driven by
  // the URL (browser back/forward, a pasted deep link), and a draft left
  // behind would be saved onto whichever account is open next.
  type GeneralDraft = {
    name?: string;
    owner?: { id: string; name: string };
    base_url?: string;
  };
  const [generalDraft, setGeneralDraft] = useState<GeneralDraft | null>(null);
  const [savingGeneral, setSavingGeneral] = useState(false);
  useEffect(() => {
    setGeneralDraft(null);
  }, [selectedAccountId]);

  // Everything the General pane needs to know about an account's address.
  // EarthRanger accounts edit a site name rather than a URL (<site>.pamdas.org);
  // a site whose stored URL isn't a pamdas one is edited as a plain URL —
  // rewriting it through the site input would silently move it to another host.
  const baseUrlStateFor = (bundle: AccountBundle) => {
    const policy = baseUrlPolicy({
      isProvider: bundle.isProvider,
      techName: bundle.techName,
      techValue: bundle.techValue || routeProvider?.type?.value,
      actions: bundle.actions,
    });
    const erSiteName = isEarthRangerDestinationType(bundle.techName)
      ? extractEarthRangerSiteName(bundle.base_url || '')
      : '';
    const asSiteName =
      isEarthRangerDestinationType(bundle.techName) &&
      (!bundle.base_url || !!erSiteName);
    return {
      ...policy,
      asSiteName,
      value: asSiteName ? erSiteName : (bundle.base_url ?? ''),
    };
  };
  const selectedBundle = selectedAccountId
    ? bundles[selectedAccountId]
    : undefined;
  const selectedBaseUrl = selectedBundle
    ? baseUrlStateFor(selectedBundle)
    : undefined;

  // General pane drafts fall back to the stored values field by field.
  const gName = generalDraft?.name ?? selectedBundle?.name ?? '';
  const gOwner = generalDraft?.owner ?? selectedBundle?.owner;
  const gUrl = generalDraft?.base_url ?? selectedBaseUrl?.value ?? '';
  const gNameError =
    generalDraft !== null && gName.trim().length === 0
      ? t('Name is required.')
      : undefined;
  // Same shared rule every other address field uses.
  const gUrlError = !selectedBaseUrl?.show
    ? undefined
    : validateBaseUrl({
        value: gUrl,
        asSiteName: selectedBaseUrl.asSiteName,
        optional: selectedBaseUrl.optional,
        t,
      });
  const generalDirty =
    generalDraft !== null &&
    !!selectedBundle &&
    (gName.trim() !== (selectedBundle.name ?? '') ||
      (!!gOwner?.id && gOwner.id !== selectedBundle.owner?.id) ||
      gUrl.trim() !== (selectedBaseUrl?.value ?? ''));

  // Resolves true when nothing about the General pane is left unsaved —
  // including when there was never a draft to begin with. Lifted out of the
  // panel so the navigation guards can save this draft too.
  const saveGeneralDraft = async (): Promise<boolean> => {
    if (generalDraft === null || !selectedBundle || !selectedBaseUrl)
      return true;
    if (!generalDirty) {
      setGeneralDraft(null);
      return true;
    }
    if (gNameError || gUrlError) return false;
    setSavingGeneral(true);
    try {
      const patch: any = {};
      if (gName.trim() !== (selectedBundle.name ?? ''))
        patch.name = gName.trim();
      if (gOwner?.id && gOwner.id !== selectedBundle.owner?.id)
        patch.owner = gOwner.id;
      if (gUrl.trim() !== (selectedBaseUrl.value ?? '')) {
        patch.base_url = selectedBaseUrl.asSiteName
          ? constructEarthRangerUrl(gUrl.trim())
          : gUrl.trim();
      }
      // A swallowed 401 comes back as undefined — not a save.
      const saved = await updateConnection(token, selectedBundle.id, patch);
      if (!saved) return false;
      toast.success(t('Connection updated'));
      setGeneralDraft(null);
      await reload(selectedBundle.id);
      return true;
    } catch (err: any) {
      console.error('failed to update connection', err);
      if (err?.response?.status !== 401) {
        toast.error(
          t('Something went wrong. Please try again or contact support.'),
        );
      }
      return false;
    } finally {
      setSavingGeneral(false);
    }
  };

  // Everything the user has stashed anywhere in this tab. The navigation guards
  // are leaving the whole selection behind, so they commit all of it rather than
  // just the section on screen.
  const savePendingEdits = async (): Promise<boolean> => {
    if (!(await saveGeneralDraft())) return false;
    const targets = Object.keys(editor.dirtyIds)
      .filter((id) => editor.dirtyIds[id])
      .map((id) => bundles[id])
      .filter(Boolean);
    for (const target of targets) {
      if (!(await editor.save(target))) return false;
    }
    return true;
  };
  const discardPendingEdits = () => {
    editor.discardAll();
    setGeneralDraft(null);
  };
  // API keys for destinations that send data back. The primary provider's key
  // arrives via props; these are fetched lazily when such a panel opens.
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({});
  // Requested ids, not just resolved ones: `selection` is rebuilt every render
  // and `arrows` changes identity on every structural rebuild, so keying only
  // off `apiKeys` fired a second request for every render while the first was
  // still in flight. Same ref-guard as sharedFetched above.
  const apiKeyFetched = useRef<Set<string>>(new Set());
  // Which accounts have a key worth asking for: the provider always, and a
  // destination once it sends data back.
  const selectedNeedsKey = selectedAccountId
    ? selectedAccountId === providerId ||
      arrows.some(
        (a) =>
          a.destinationId === selectedAccountId &&
          a.direction === 'in' &&
          a.state === 'enabled',
      )
    : false;
  // Lazy, and only for the CONNECT section: every other section of the panel
  // opens without spending a key request (the provider's used to be fetched on
  // every Route page load, whatever tab the user was on).
  const openAccountSection =
    selection.kind === 'account' ? selection.section : undefined;
  useEffect(() => {
    if (!token || !selectedAccountId || openAccountSection !== 'how-to-connect')
      return;
    if (!selectedNeedsKey || apiKeyFetched.current.has(selectedAccountId))
      return;
    const id = selectedAccountId;
    apiKeyFetched.current.add(id);
    // No cancelled-guard: the write is keyed by id, so storing a late response
    // is always safe — and skipping it while the id stayed latched left the
    // section on "Loading…" for the rest of the page's life.
    getRouteApiKey(token, id)
      .then((r: any) => {
        if (r?.api_key) {
          setApiKeys((prev) => ({ ...prev, [id]: r.api_key }));
          return;
        }
        // No key and no throw: a swallowed 401 resolves undefined. Un-mark
        // it so reopening the section after logging back in tries again,
        // instead of showing "Loading…" until a full reload.
        apiKeyFetched.current.delete(id);
      })
      .catch((e: any) => {
        apiKeyFetched.current.delete(id);
        console.error('failed to load API key for account', id, e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedAccountId, openAccountSection, selectedNeedsKey]);

  const accountPanel = () => {
    if (selection.kind !== 'account') return null;
    const bundle = bundles[selection.integrationId];
    if (!bundle) {
      return (
        <div className="rounded-lg border border-unlisted-gray-lines bg-white p-5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="mb-3 h-4 w-48 animate-pulse rounded bg-teal-t30"
            />
          ))}
        </div>
      );
    }
    const dest = destinations.find((d: any) => d.id === bundle.id);

    // Every action the account owns is configured here, grouped by what it
    // does: credentials, then the directions data can move (pull = this
    // system sends, push = it receives), with anything else under Advanced
    // Config at the bottom. The account's name heads the list — a Connection
    // can be renamed from either side of the map.
    const withFields = bundle.actions.filter(isActionVisible);
    const allActions = withFields;
    // One General pane carries the account's name, workspace and endpoint.
    // Whether the endpoint shows, and whether it may stay empty, is the shared
    // base-URL policy's call.
    const urlState =
      bundle.id === selectedBundle?.id && selectedBaseUrl
        ? selectedBaseUrl
        : baseUrlStateFor(bundle);
    // The action grouping is the shared one, so this panel and the Connection
    // page can't drift apart (a hand-rolled copy here put an auth action typed
    // `pull` in both Credentials and Send data).
    // A flow direction no enabled arrow is carrying is dormant: its sections
    // show as optional FYIs instead of carrying "!" badges — the same rule the
    // node badge and the Quick Start count already apply in computeNeedsSetup.
    const dormant = dormantFlowTypes(bundle.id, arrows);
    // GUNDI-5680 temporary hack: sections that never badge on this account
    // given who is at the other end of the Route (see tempExemptions.ts).
    const exemptSlugs = exemptSlugsFor(bundle, {
      providerId,
      routeProvider,
      bundles,
      destinations,
    });
    const nav: NavSection[] = buildActionNav({
      target: bundle,
      actions: withFields,
      t,
      detailsLabel: t('General'),
      detailsSlug: 'general',
      detailsNeedsSetup:
        urlState.show && !urlState.optional && !bundle.base_url,
      dormantTypes: dormant,
      exemptSlugs,
    });
    // A destination that sends data back is a data provider too — its
    // developers need its own API key, not just the primary provider's.
    const sendsBack =
      !bundle.isProvider &&
      arrows.some(
        (a) =>
          a.destinationId === bundle.id &&
          a.direction === 'in' &&
          a.state === 'enabled',
      );
    const providerType = bundle.isProvider ? routeProvider?.type : undefined;
    // Webhook providers keep the webhook endpoint + headers; everything else
    // gets the sensors endpoint. A type that carries a webhook is a webhook
    // here even when it also has pull config (kind would say "standard").
    const routeKind =
      providerType &&
      (getRouteKind(providerType) === 'webhook' || providerType.webhook)
        ? 'webhook'
        : 'api-push';
    // Webhook payload parsing is provider-side config, so it lives on this
    // panel (it used to be its own tab).
    const webhookConfig =
      bundle.isProvider &&
      (routeProvider?.webhook_configuration || routeProvider?.type?.webhook)
        ? (routeProvider.webhook_configuration ?? {
            id: '',
            integration: routeProvider.id,
            webhook: routeProvider.type?.webhook ?? {
              id: '',
              name: '',
              value: '',
            },
            data: null,
          })
        : undefined;
    // Insert ahead of the catch-all group so Advanced Config stays last.
    // Re-found per insert: splicing shifts the catch-all group's index, and a
    // cached one put the second insert ahead of the first (CONNECT before
    // WEBHOOK) instead of both before Advanced Config.
    const insertBeforeAdvanced = (section: NavSection) => {
      const advancedAt = nav.findIndex((s) => s.cat === t(ADVANCED_CAT));
      if (advancedAt === -1) nav.push(section);
      else nav.splice(advancedAt, 0, section);
    };
    if (webhookConfig) {
      insertBeforeAdvanced({
        cat: t('WEBHOOK'),
        // GUNDI-5680 temporary hack: never badged on InReach, where payload
        // parsing is not required (see tempExemptions.ts).
        items: [
          {
            slug: 'webhook',
            label: t('Payload parsing'),
            needsSetup:
              !routeProvider?.webhook_configuration?.data &&
              !payloadParsingNotRequired(routeProvider?.type?.value),
          },
        ],
      });
    }
    // Last section, and present on every account that acts as a data
    // provider: the primary provider always, and a destination once it sends
    // data back — both sides of a bidirectional Route have their own API key.
    if (providerType || sendsBack) {
      insertBeforeAdvanced({
        cat: t('CONNECT'),
        items: [{ slug: 'how-to-connect', label: t('API key') }],
      });
    }

    // Old bookmarks may still address the split sections, and a section the
    // URL names but this account doesn't offer would render an empty panel —
    // fall back to the first section either way.
    const requestedSlug =
      selection.section === 'name' || selection.section === 'endpoint'
        ? 'general'
        : selection.section;
    const navSlugs = nav.flatMap((section) =>
      section.items.map((item) => item.slug),
    );
    const activeSlug =
      (requestedSlug && navSlugs.includes(requestedSlug)
        ? requestedSlug
        : undefined) || nav[0]?.items[0]?.slug;
    const activeAction = allActions.find((a) => slugOf(a) === activeSlug);
    // Saving is per section, so Save/Cancel act on the active section only —
    // the section-switch prompt guarantees no other section is dirty.
    const isDirty =
      !!activeAction && editor.isActionDirty(bundle.id, activeAction.id);
    const onWebhook = activeSlug === 'webhook' && !!webhookConfig;
    // An action that was never saved, or that is still missing required
    // fields, can be committed with the values on screen, so Save is live
    // even before the user pokes a field.
    // The editor's own helper, not actionCanCommitForm directly: it adds the
    // "nothing to commit for a never-saved section that is switched off"
    // case, and a second copy of the rule here went out of step with it.
    const canCommitDefaults = editor.canCommitDefaults(bundle, activeAction);

    // Section switches inside the account ask the Save-or-discard question
    // whenever the section being left has unsaved work.
    const goToSection = (slug: string) =>
      select({ kind: 'account', integrationId: bundle.id, section: slug });
    const selectSection = (slug: string) => {
      if (slug === activeSlug) return;
      if (activeSlug === 'general' && generalDirty) {
        return setSectionPrompt({
          onSave: saveGeneralDraft,
          onDiscard: () => setGeneralDraft(null),
          proceed: () => goToSection(slug),
        });
      }
      if (activeAction && editor.isActionDirty(bundle.id, activeAction.id)) {
        return setSectionPrompt({
          onSave: () => editor.save(bundle, activeAction.id),
          onDiscard: () => editor.cancel(bundle, activeAction.id),
          proceed: () => goToSection(slug),
        });
      }
      goToSection(slug);
    };
    return (
      <SelectionPanel
        title={
          <span className="flex min-w-0 flex-col gap-1">
            <span className="truncate">{bundle.name}</span>
            <span className="flex items-center gap-8 text-sm font-normal">
              {/* The account's own screen: every Route using it, and the
                            record itself outside this Route's context. Routed through
                            the same Save-or-discard prompt as a selection change. */}
              <button
                type="button"
                data-testid="ConfigurationsTab-view-connection-button"
                onClick={() =>
                  guardedNavigate(`/connections/${bundle.id}/configuration`)
                }
                className={`${buttonClasses.inlineLink} flex items-center gap-1 no-underline`}
              >
                {t('View Connection')}
                <OpenInNewIcon />
              </button>
              <CopyIdButton
                key={bundle.id}
                id={bundle.id}
                testId="ConfigurationsTab-copy-id-button"
                label={t('Copy Connection ID')}
              />
            </span>
          </span>
        }
        headerActions={
          <>
            {!bundle.isProvider && dest && (
              <button
                className={buttonClasses.headerActionRed}
                onClick={() =>
                  setConfirmation({
                    title: t('Remove from Route'),
                    body: t(
                      'Remove {{name}} from this Route? The site itself is not deleted — it just stops receiving data from this flow.',
                      { name: bundle.name },
                    ),
                    confirmLabel: t('Remove'),
                    destructive: true,
                    onConfirm: () => {
                      Promise.resolve(props.onRemoveSite(dest)).then(() =>
                        select({ kind: 'none' }),
                      );
                    },
                  })
                }
              >
                {t('Remove from Route')}
              </button>
            )}
          </>
        }
        banner={
          <SharedAccountNotice
            accountName={bundle.name}
            usedBy={
              bundle.isProvider
                ? props.providerSharedWith || []
                : sharedBySite[bundle.id] || []
            }
          />
        }
        nav={nav}
        activeSlug={activeSlug}
        onSelectSection={selectSection}
        emptyNavNote={t('This account has no general configuration.')}
        footer={
          onWebhook ? (
            currentMemberIsAdmin ? (
              <>
                {/* This editor keeps its own draft, so the panel reaches the
                        save through a handle rather than lifting all of it. */}
                <button
                  className={buttonClasses.primary}
                  disabled={!props.webhookChangesMade || savingWebhook}
                  data-testid="RouteDetailsWebhook-save-button"
                  onClick={async () => {
                    setSavingWebhook(true);
                    try {
                      await webhookRef.current?.save();
                    } finally {
                      setSavingWebhook(false);
                    }
                  }}
                >
                  {t('Save')}
                </button>
                <button
                  className={buttonClasses.cancel}
                  disabled={!props.webhookChangesMade}
                  data-testid="RouteDetailsWebhook-cancel-button"
                  onClick={() => props.onCancelWebhook?.()}
                >
                  {t('Cancel')}
                </button>
              </>
            ) : undefined
          ) : activeSlug === 'general' ? (
            <>
              <button
                className={buttonClasses.primary}
                disabled={
                  !generalDirty || !!gNameError || !!gUrlError || savingGeneral
                }
                onClick={saveGeneralDraft}
              >
                {t('Save')}
              </button>
              <button
                className={buttonClasses.cancel}
                disabled={generalDraft === null}
                onClick={() => setGeneralDraft(null)}
              >
                {t('Cancel')}
              </button>
            </>
          ) : activeAction ? (
            <>
              <button
                className={buttonClasses.primary}
                disabled={!isDirty && !canCommitDefaults}
                onClick={() => {
                  editor.commitDefaults(bundle, activeAction);
                  editor.save(bundle, activeAction.id);
                }}
              >
                {t('Save')}
              </button>
              <button
                className={buttonClasses.cancel}
                disabled={!isDirty}
                onClick={() => editor.cancel(bundle, activeAction.id)}
              >
                {t('Cancel')}
              </button>
            </>
          ) : undefined
        }
      >
        {activeSlug === 'general' ? (
          <div className="rounded border border-ui-field-outline bg-white p-4">
            <div className="mb-2 flex w-full flex-col gap-2 border-b border-ui-divider-lines pb-3 md:flex-row md:items-start md:justify-between md:gap-6">
              <div className="flex flex-col md:w-1/2">
                <label htmlFor="account_name" className="mr-1">
                  {t('Name')} *
                </label>
                <span className="mt-1 text-sm text-ui-text-primary-text">
                  {t('A descriptive name for this Connection.')}
                </span>
              </div>
              <div className="md:w-1/2">
                <input
                  id="account_name"
                  data-testid="ConfigurationsTab-account-name-input"
                  value={gName}
                  onChange={(e) =>
                    setGeneralDraft((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  className={`w-full rounded border p-1.5 px-2 transition-colors duration-300 ${
                    gNameError
                      ? 'border-unlisted-orange'
                      : 'border-unlisted-neutral-gray-200'
                  }`}
                />
                {gNameError && (
                  <p className="mt-1 text-sm text-unlisted-orange-600">
                    {gNameError}
                  </p>
                )}
              </div>
            </div>
            <div className="mb-2 flex w-full flex-col gap-2 border-b border-ui-divider-lines pb-3 md:flex-row md:items-start md:justify-between md:gap-6">
              <div className="flex flex-col md:w-1/2">
                <label htmlFor="account_workspace" className="mr-1">
                  {t('Workspace')} *
                </label>
                <span className="mt-1 text-sm text-ui-text-primary-text">
                  {t('The workspace this Connection belongs to.')}
                </span>
              </div>
              <div className="md:w-1/2">
                <SelectInput
                  inputId="account_workspace"
                  options={(props.organizations || []).map((org: any) => ({
                    label: org.name,
                    value: org.id,
                  }))}
                  className={'basic-single'}
                  value={
                    gOwner?.id ? { label: gOwner.name, value: gOwner.id } : null
                  }
                  isDisabled={false}
                  isLoading={false}
                  isClearable={false}
                  isSearchable={true}
                  isMulti={false}
                  name={t('Workspace')}
                  onChange={(e: any) =>
                    setGeneralDraft((prev) => ({
                      ...prev,
                      owner: { id: e.value, name: e.label },
                    }))
                  }
                  onMenuScrollToBottom={props.onLoadMoreOrgs}
                  handleInputChange={props.onSearchOrgs}
                />
              </div>
            </div>
            {urlState.show && (
              <div className="flex w-full flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-6">
                <div className="flex flex-col md:w-1/2">
                  <label htmlFor="account_base_url" className="mr-1">
                    {urlState.asSiteName ? t('Site URL') : t('URL')}
                    {urlState.optional ? '' : ' *'}
                  </label>
                  <span className="mt-1 text-sm text-ui-text-primary-text">
                    {urlState.asSiteName
                      ? t('The name of the EarthRanger site (e.g. my-site).')
                      : t('The address Gundi uses to reach this system.')}
                  </span>
                </div>
                <div className="md:w-1/2">
                  {urlState.asSiteName ? (
                    <EarthRangerSiteInput
                      value={gUrl}
                      onChange={(e: any) =>
                        setGeneralDraft((prev) => ({
                          ...prev,
                          base_url: e.target.value,
                        }))
                      }
                      error={gUrlError}
                    />
                  ) : (
                    <>
                      <input
                        id="account_base_url"
                        value={gUrl}
                        onChange={(e) =>
                          setGeneralDraft((prev) => ({
                            ...prev,
                            base_url: e.target.value,
                          }))
                        }
                        className={`w-full rounded border p-1.5 px-2 transition-colors duration-300 ${
                          gUrlError
                            ? 'border-unlisted-orange'
                            : 'border-unlisted-neutral-gray-200'
                        }`}
                      />
                      {gUrlError && (
                        <p className="mt-1 text-sm text-unlisted-orange-600">
                          {gUrlError}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : activeSlug === 'how-to-connect' ? (
          <HowToConnect
            kind={bundle.isProvider ? routeKind : 'api-push'}
            integrationType={
              (bundle.isProvider
                ? routeProvider?.type?.value
                : dest?.type?.value) || ''
            }
            apiKey={apiKeys[bundle.id]}
            isAdmin={currentMemberIsAdmin}
          />
        ) : activeSlug === 'webhook' && webhookConfig ? (
          <RouteDetailsWebhook
            // The footer's Save reaches the editor's draft through
            // this handle.
            ref={webhookRef}
            webhookConfiguration={webhookConfig}
            currentMemberIsAdmin={currentMemberIsAdmin}
            onSave={props.onSaveWebhook}
            onCancel={props.onCancelWebhook}
            changesMade={props.webhookChangesMade}
            onChangesMade={props.onWebhookChangesMade}
          />
        ) : activeAction ? (
          <>
            {isDormantAction(activeAction, dormant) && (
              /* This direction isn't carrying data, so nothing here is
                                       required yet — say so, or the empty required fields
                                       read as work the user owes right now. Two reasons a
                                       direction can be dormant, two different truths: a paused
                                       Route's main direction is not "the other direction". */
              <p className="mb-3 rounded border border-unlisted-gray-lines px-3 py-2 text-sm text-ui-text-primary-text">
                {routeEnabled
                  ? t(
                      'These settings are not required for the current data flow. Configure them only if you enable data flowing in the other direction.',
                    )
                  : t(
                      "This Route is paused — these settings apply when it's turned back on.",
                    )}
              </p>
            )}
            {renderActionForm(
              bundle,
              activeAction,
              true,
              isAuth(activeAction) ||
                flowActionCounts(activeAction, dormant, exemptSlugs),
            )}
          </>
        ) : (
          <p className="text-sm text-ui-text-primary-text">
            {t('This account has no general configuration.')}
          </p>
        )}
      </SelectionPanel>
    );
  };

  const flowPanel = () => {
    if (selection.kind !== 'flow') return null;
    const arrow = arrows.find(
      (a) => a.id === `${selection.destinationId}-${selection.direction}`,
    );
    const providerBundle = bundles[providerId];
    const destBundle = bundles[selection.destinationId];
    const dest = destinations.find(
      (d: any) => d.id === selection.destinationId,
    );
    if (!arrow || !providerBundle || !destBundle) {
      return (
        <div className="rounded-lg border border-unlisted-gray-lines bg-white p-5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="mb-3 h-4 w-48 animate-pulse rounded bg-teal-t30"
            />
          ))}
        </div>
      );
    }

    const sender = arrow.direction === 'out' ? providerBundle : destBundle;
    const receiver = arrow.direction === 'out' ? destBundle : providerBundle;
    // Pull/push settings are configured on the accounts that own them; the
    // arrow carries what belongs to the flow itself — its on/off state and its
    // field-mapping rules. Both directions have rules, but the incoming one
    // keeps them on the reverse Route's own record: until that Route exists
    // (the direction is off) there is nothing to attach a rule to.
    const nav: NavSection[] = arrowOffersRules({
      direction: arrow.direction,
      state: arrow.state,
      reverseRecordExists: !!props.reverseRuleEditable?.(
        selection.destinationId,
      ),
    })
      ? [
          {
            cat: t('RULES'),
            items: [{ slug: 'field-mapping', label: t('Field mapping') }],
          },
        ]
      : [];

    const activeSlug = selection.section || nav[0]?.items[0]?.slug;

    const headerActions =
      arrow.direction === 'out' ? (
        // The whole Route's on/off switch lives here — same gesture as
        // disabling a reverse flow, one arrow to the right of it.
        routeEnabled ? (
          <span className="flex items-center gap-2">
            <span className="text-xs text-ui-text-primary-text">
              {t('Sending')}
            </span>
            <CustomCheckbox
              label={t('Sending')}
              formData={true}
              onChange={() =>
                setConfirmation({
                  title: t('Stop sending data'),
                  // `enabled` is a field on the provider Connection, not on the
                  // route record, so this stops every Route that Connection
                  // feeds — don't claim it only affects this one.
                  // null = the "which other Routes" lookup failed, so the
                  // reassuring single-Route wording would be a guess.
                  body:
                    props.providerAlsoProvidesFor === null
                      ? t(
                          'Stop sending data from {{name}}? This Connection may provide data to other Routes, which would stop too. The configuration itself is kept.',
                          { name: providerBundle.name },
                        )
                      : (props.providerAlsoProvidesFor ?? 0) > 0
                        ? t(
                            'Stop sending data from {{name}}? It also provides data to {{count}} other Route, which stops too. The configuration itself is kept.',
                            {
                              name: providerBundle.name,
                              count: props.providerAlsoProvidesFor,
                            },
                          )
                        : t(
                            'Stop sending data from {{name}}? Data stops flowing to every destination of this Route. The configuration itself is kept.',
                            { name: providerBundle.name },
                          ),
                  confirmLabel: t('Stop sending'),
                  destructive: true,
                  onConfirm: () => props.onToggleRouteEnabled?.(false),
                })
              }
            />
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <span className="text-xs text-ui-text-primary-text">
              {t('Enable')}
            </span>
            <CustomCheckbox
              label={t('Enable')}
              formData={false}
              onChange={() => props.onToggleRouteEnabled?.(true)}
            />
          </span>
        )
      ) : arrow.state === 'enabled' ? (
        <span className="flex items-center gap-2">
          <span className="text-xs text-ui-text-primary-text">
            {t('Sending')}
          </span>
          <CustomCheckbox
            label={t('Sending')}
            formData={true}
            onChange={() =>
              dest &&
              setConfirmation({
                title: t('Stop sending data'),
                body: t(
                  'Stop sending data from {{from}} to {{to}}? This removes the reverse Route; the configuration itself is kept.',
                  { from: destBundle.name, to: providerBundle.name },
                ),
                confirmLabel: t('Stop sending'),
                destructive: true,
                onConfirm: () => {
                  props.onDisableReverseFlow(dest);
                },
              })
            }
          />
        </span>
      ) : arrow.state === 'disabled' ? (
        <span className="flex items-center gap-2">
          <span className="text-xs text-ui-text-primary-text">
            {t('Enable')}
          </span>
          <CustomCheckbox
            label={t('Enable')}
            formData={false}
            onChange={() => {
              if (dest) props.onEnableReverseFlow(dest);
            }}
          />
        </span>
      ) : undefined;

    const note =
      arrow.direction === 'in' && arrow.state === 'disabled'
        ? t(
            'Not enabled yet. Configure both accounts on the map, then turn this direction on.',
          )
        : undefined;

    const arrowRouteId: string | undefined =
      arrow.direction === 'out'
        ? routeRecordId(route)
        : (props.reverseRoutesBySite?.[selection.destinationId] || [])[0];

    return (
      <SelectionPanel
        title={
          <span className="flex min-w-0 flex-col gap-1">
            <span className="truncate">{`${sender.name} → ${receiver.name}`}</span>
            {/* The record this arrow stands for: the Route itself going
                        out, the separate reverse Route coming back (none until
                        that direction has been enabled). */}
            {arrowRouteId && (
              <span className="flex items-center text-sm font-normal">
                <CopyIdButton
                  key={arrowRouteId}
                  id={arrowRouteId}
                  testId="ConfigurationsTab-copy-route-id-button"
                  label={t('Copy Route ID')}
                />
              </span>
            )}
          </span>
        }
        subtitle={
          note ? (
            <span className="text-xs text-ui-text-primary-text">{note}</span>
          ) : undefined
        }
        headerActions={headerActions}
        banner={
          arrow.state === 'not-enableable' && arrow.reason ? (
            // Same amber treatment as SharedAccountNotice — it's a warning, not a neutral note.
            <div className="mb-5 rounded border border-unlisted-orange-200 bg-unlisted-orange-50 px-4 py-3 text-sm text-ui-text-primary-text">
              {arrow.reason}
            </div>
          ) : undefined
        }
        nav={nav}
        activeSlug={activeSlug}
        onSelectSection={(slug) =>
          select({
            kind: 'flow',
            destinationId: selection.destinationId,
            direction: selection.direction,
            section: slug,
          })
        }
        emptyNavNote={t('Nothing to configure for this direction.')}
      >
        {activeSlug === 'field-mapping' ? (
          fieldMappingSection(dest, arrow.direction)
        ) : (
          <p className="text-sm text-ui-text-primary-text">
            {t(
              'Pull and push settings are configured on the accounts — click a node on the map.',
            )}
          </p>
        )}
      </SelectionPanel>
    );
  };

  return (
    <div className="mt-8 px-0 pb-28 md:px-4">
      {failed && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded border border-unlisted-orange-200 bg-unlisted-orange-50 px-4 py-3 text-sm text-ui-text-primary-text">
          <span>
            {t(
              "Some accounts in this Route couldn't be loaded, so parts of the map may be incomplete.",
            )}
          </span>
          <button
            type="button"
            className={`${buttonClasses.lightGreen} ml-auto`}
            onClick={() => reload()}
          >
            {t('Retry')}
          </button>
        </div>
      )}
      {selection.kind === 'none' && (
        <p className={`${onTealText} mb-3`}>
          {loading
            ? t('Loading accounts…')
            : t('Select an account or a flow below to configure it.')}
        </p>
      )}
      <RouteFlowMap
        nodes={nodes}
        edges={edges}
        destinationCount={destinations.length}
        vertical={isMobile}
        routeStatus={route?.status}
        routeEnabled={routeProvider?.enabled !== false}
        needsSetup={
          !loading && anyBundleLoaded && structural.needsSetupCount > 0
        }
        logsPath={routeUrlId ? `/routes/${routeUrlId}/logs` : undefined}
        onNavigateToLogs={guardedNavigate}
        onSelectAccount={(id: string) =>
          guardedSelect({ kind: 'account', integrationId: id })
        }
        onSelectFlow={(destinationId: string, direction: FlowDirectionKey) =>
          guardedSelect({ kind: 'flow', destinationId, direction })
        }
        onAddDestination={props.onAddDestination}
        onEnableRoute={() => props.onToggleRouteEnabled?.(true)}
        highlightAddDestination={!!props.highlightAddDestination}
      />
      {confirmation && (
        <ModalShell
          title={confirmation.title}
          onClose={() => setConfirmation(null)}
          footer={
            <>
              <button
                type="button"
                className={
                  confirmation.destructive
                    ? buttonClasses.destructive
                    : buttonClasses.primary
                }
                onClick={() => {
                  const run = confirmation.onConfirm;
                  setConfirmation(null);
                  run();
                }}
              >
                {confirmation.confirmLabel}
              </button>
              <button
                type="button"
                className={buttonClasses.cancel}
                onClick={() => setConfirmation(null)}
              >
                {t('Cancel')}
              </button>
            </>
          }
        >
          <p className="text-base text-ui-text-primary-text">
            {confirmation.body}
          </p>
        </ModalShell>
      )}
      <UnsavedSectionPrompt
        prompt={sectionPrompt}
        onClose={() => setSectionPrompt(null)}
      />
      {selection.kind === 'account' && accountPanel()}
      {selection.kind === 'flow' && flowPanel()}
    </div>
  );
};

export default ConfigurationsTab;
